import * as Sentry from "@sentry/nextjs"

/**
 * Centralised Sentry capture for chat-related errors / events.
 *
 * Lives in the marketing-site project (same DSN as everything else)
 * because the code being instrumented is in this repo. Filter in
 * Sentry on the `area:chat` tag.
 *
 * All helpers tolerate being called server-side (`/api/chat/*` route
 * handlers) and client-side (panel / hooks).
 */

export interface ChatContext {
  scope?: string
  conversation_id?: number | null
  country?: string
  locale?: string
  prompt_variant?: string
  handed_off?: boolean
  entry_point?: "inline" | "launcher"
  /** Free-form extra fields surfaced under the `chat` Sentry context. */
  extra?: Record<string, unknown>
}

function buildOptions(ctx: ChatContext = {}) {
  const tags: Record<string, string> = { area: "chat" }
  if (ctx.scope) tags["chat.scope"] = ctx.scope
  if (ctx.country) tags["chat.country"] = ctx.country
  if (ctx.locale) tags["chat.locale"] = ctx.locale
  if (ctx.prompt_variant) tags["chat.prompt_variant"] = ctx.prompt_variant
  if (typeof ctx.handed_off === "boolean")
    tags["chat.handed_off"] = String(ctx.handed_off)
  if (ctx.entry_point) tags["chat.entry_point"] = ctx.entry_point
  return {
    tags,
    contexts: {
      chat: {
        scope: ctx.scope,
        conversation_id: ctx.conversation_id,
        country: ctx.country,
        locale: ctx.locale,
        prompt_variant: ctx.prompt_variant,
        handed_off: ctx.handed_off,
        entry_point: ctx.entry_point,
        ...ctx.extra,
      },
    },
  }
}

export function captureChatError(err: unknown, ctx: ChatContext = {}): void {
  Sentry.captureException(err, buildOptions(ctx))
}

export function captureChatMessage(
  message: string,
  level: "fatal" | "error" | "warning" | "info" | "debug",
  ctx: ChatContext = {}
): void {
  Sentry.captureMessage(message, { level, ...buildOptions(ctx) })
}

export function addChatBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({
    category: `chat.${category}`,
    message,
    data,
    level: "info",
  })
}
